import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";

import { Row, Col, Badge } from "reactstrap";
import { restartWithToast } from "../services/machineOperationsService";
import triggerOnPremRemoteCommand from "../services/remoteControlService";
import "./ServersServices.scss";

const ALLOWED_TO_RESTART_CONTAINERS = ["simone-inference", "replay_api"];

/** Parse version from "update-dvr X.Y.Z" CLI output. */
function parseUpdateDvrVersion(stdout) {
  if (typeof stdout !== "string" || !stdout.trim()) return null;
  const parts = stdout.trim().split(/\s+/);
  return parts.length >= 2 ? parts[parts.length - 1] : parts[0] || null;
}

const ServerServices = ({ server, serverName }) => {
  const [updateDvrVersion, setUpdateDvrVersion] = useState(null);
  const [updateDvrLoading, setUpdateDvrLoading] = useState(true);
  const [installLoading, setInstallLoading] = useState(false);
  const restarting = useRef(new Set());

  const fetchUpdateDvrVersion = useCallback(async () => {
    if (!serverName) return;
    setUpdateDvrLoading(true);
    try {
      const data = await triggerOnPremRemoteCommand({
        hostname: serverName,
        cmd: "get-update-dvr-version",
        cmdArgs: {},
      });
      const version =
        data?.status?.toLowerCase() === "ok" && typeof data?.stdout === "string"
          ? parseUpdateDvrVersion(data.stdout)
          : null;
      setUpdateDvrVersion(version);
    } catch {
      setUpdateDvrVersion(null);
    } finally {
      setUpdateDvrLoading(false);
    }
  }, [serverName]);

  useEffect(() => {
    fetchUpdateDvrVersion();
  }, [fetchUpdateDvrVersion]);

  const isUpdateDvrInstalled = Boolean(updateDvrVersion);

  async function installUpdateDvr() {
    if (installLoading) return;
    setInstallLoading(true);
    const isInstall = !isUpdateDvrInstalled;
    try {
      const data = await triggerOnPremRemoteCommand({
        hostname: serverName,
        cmd: "update-dvr",
        cmdArgs: { type_action: "install" },
      });
      if (data.status?.toLowerCase() === "ok") {
        toast.success(isInstall ? "update-dvr installed successfully" : "update-dvr updated successfully");
        await fetchUpdateDvrVersion();
      } else {
        toast.error(isInstall ? "update-dvr installation failed" : "update-dvr update failed");
      }
    } catch {
      toast.error(isInstall ? "Failed to install update-dvr" : "Failed to update update-dvr");
    } finally {
      setInstallLoading(false);
    }
  }

  async function restartContainer(container) {
    if (restarting.current.has(container)) return;
    restarting.current.add(container);
    try {
      await restartWithToast([serverName], container);
      await sleep(10000);
    } finally {
      restarting.current.delete(container);
    }
  }
  function RenderImage({ status }) {
    if (status === "OK") {
      return <i className="fas fa-check-circle text-success" />;
    }
    return <i className="fas fa-times-circle text-danger" />;
  }

  function ButtonRestart({ container }) {
    if (ALLOWED_TO_RESTART_CONTAINERS.includes(container)) {
      return (
        <Badge className="btn-restart btn-primary btn" onClick={() => restartContainer(container)}>
          <i className="fas fa-power-off mr-2" /> Restart
        </Badge>
      );
    }
    return "";
  }
  const DateParsing = ({ datetime }) => {
    if (datetime === null) {
      return "Down";
    }
    const dateA = new Date(datetime);
    return dateA.toLocaleString("en-GB");
  };
  function ContainerParsing({ containerName }) {
    if (containerName === "simone-inference") {
      return <i className="fas fa-stroopwafel mr-2" />;
    }
    if (containerName === "replay_api") {
      return <i className="fas fa-play-circle mr-2" />;
    }
    if (containerName === "hd_upload") {
      return <i className="fas fa-cloud-upload-alt mr-2" />;
    }
    if (containerName === "update-dvr") {
      return <i className="fas fa-cog mr-2" />;
    }
    return "";
  }
  function BadgeVersion({ image }) {
    if (image === "" || typeof image === "undefined") {
      return <Badge>Unknown</Badge>;
    }
    return <Badge>{image.split(":")[1]}</Badge>;
  }
  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const containerRows = Object.keys(server).map(
    (container) =>
      server[container].image && (
        <div key={container} className="servers-service">
          <Row>
            <Col md={3}>
              <ContainerParsing containerName={container} />
              {container}
            </Col>
            <Col md={3}>
              <BadgeVersion image={server[container].image} />
            </Col>
            <Col md={1}>
              <RenderImage status={server[container].status} />
            </Col>
            <Col md={2}>
              <DateParsing datetime={server[container].uptime} />
            </Col>
            <Col md={3}>
              <ButtonRestart container={container} />
            </Col>
          </Row>
        </div>
      )
  );

  const updateDvrRow = (
    <div key="update-dvr" className="servers-service">
      <Row>
        <Col md={3}>
          <ContainerParsing containerName="update-dvr" />
          update-dvr
        </Col>
        <Col md={3}>
          <Badge>{updateDvrLoading ? "..." : updateDvrVersion || "Unknown"}</Badge>
        </Col>
        <Col md={1}>-</Col>
        <Col md={2}>-</Col>
        <Col md={3}>
          <Badge
            className="btn-restart btn-primary btn"
            onClick={installUpdateDvr}
            style={{ cursor: installLoading ? "not-allowed" : "pointer", opacity: installLoading ? 0.7 : 1 }}
          >
            {installLoading ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2" />
                {isUpdateDvrInstalled ? "Updating..." : "Installing..."}
              </>
            ) : (
              <>
                <i className={isUpdateDvrInstalled ? "fas fa-sync-alt mr-2" : "fas fa-download mr-2"} />
                {isUpdateDvrInstalled ? "Update" : "Install"}
              </>
            )}
          </Badge>
        </Col>
      </Row>
    </div>
  );

  return [...containerRows.filter(Boolean), updateDvrRow];
};
export default ServerServices;
